import React, { useEffect, useRef, useState, useCallback } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, getToken, fileStreamUrl } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import {
  Heart, MessageCircle, Share2, ChevronUp, ChevronDown, X, Upload as UploadIcon,
  Volume2, VolumeX, Play, Pause, AlertTriangle, Link as LinkIcon, Music,
} from "lucide-react";

const isYouTubeUrl = (u) => typeof u === "string" && /youtube\.com\/embed\/|youtu\.be\//.test(u);
const isTikTokUrl  = (u) => typeof u === "string" && /tiktok\.com/.test(u);
const isInstaUrl   = (u) => typeof u === "string" && /instagram\.com/.test(u);

export const ShortsPage = () => {
  const { user } = useAuth();
  const [shorts, setShorts] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(0);
  const [muted, setMuted] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const navigate = useNavigate();
  const [params] = useSearchParams();

  useEffect(() => { document.title = "Shorts — Voamada"; }, []);

  const load = useCallback(async (p = 1) => {
    try {
      setLoading(p === 1);
      const { data } = await api.get(`/shorts?page=${p}`);
      setShorts((prev) => p === 1 ? data.shorts : [...prev, ...data.shorts]);
      setHasMore(data.has_more);
      setPage(p);
    } catch {
      toast.error("Erreur chargement des shorts");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(1); }, [load]);

  // Si query ?id=xxx → ouvre directement
  useEffect(() => {
    const id = params.get("id");
    if (!id || shorts.length === 0) return;
    const idx = shorts.findIndex((s) => s.track_id === id);
    if (idx >= 0) setActive(idx);
  }, [params, shorts]);

  // Pagination auto quand on approche de la fin
  useEffect(() => {
    if (active >= shorts.length - 3 && hasMore && !loading) load(page + 1);
  }, [active, shorts.length, hasMore, loading, page, load]);

  // Gestion swipe haut/bas + flèches
  const containerRef = useRef(null);
  useEffect(() => {
    const handler = (e) => {
      if (showCreate) return;
      if (e.key === "ArrowUp")   setActive((i) => Math.max(0, i - 1));
      if (e.key === "ArrowDown") setActive((i) => Math.min(shorts.length - 1, i + 1));
      if (e.key === "Escape")    navigate(-1);
      if (e.key === "m" || e.key === "M") setMuted((m) => !m);
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [shorts.length, showCreate, navigate]);

  // Touch swipe
  const touchStartY = useRef(0);
  const onTouchStart = (e) => { touchStartY.current = e.touches[0].clientY; };
  const onTouchEnd = (e) => {
    const dy = e.changedTouches[0].clientY - touchStartY.current;
    if (Math.abs(dy) < 50) return;
    if (dy < 0) setActive((i) => Math.min(shorts.length - 1, i + 1));
    else        setActive((i) => Math.max(0, i - 1));
  };

  const current = shorts[active];

  return (
    <div
      ref={containerRef}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
      className="fixed inset-0 bg-black z-40 overflow-hidden"
      data-testid="shorts-page"
    >
      {/* Top bar */}
      <div className="absolute top-0 left-0 right-0 z-30 flex items-center justify-between p-4 bg-gradient-to-b from-black/80 to-transparent">
        <button
          onClick={() => navigate("/")}
          className="p-2 -ml-1 rounded-full hover:bg-white/15 text-white transition"
          data-testid="shorts-back"
        >
          <X size={22} />
        </button>
        <p className="font-black text-xl uppercase tracking-widest text-white" style={{ fontFamily: "'Anton', sans-serif" }}>Shorts</p>
        <button
          onClick={() => user ? setShowCreate(true) : navigate("/login?redirect=/shorts")}
          className="px-3.5 py-2 rounded-full bg-brand-red hover:bg-brand-redHover text-white text-xs font-black uppercase tracking-widest transition shadow-lg shadow-brand-red/30"
          data-testid="shorts-create-btn"
        >
          + Publier
        </button>
      </div>

      {/* Feed */}
      {loading && shorts.length === 0 ? (
        <div className="absolute inset-0 flex items-center justify-center text-white/50">Chargement…</div>
      ) : shorts.length === 0 ? (
        <EmptyState onCreate={() => user ? setShowCreate(true) : navigate("/login?redirect=/shorts")} />
      ) : (
        <ShortPlayer
          short={current}
          isActive={true}
          muted={muted}
          onToggleMute={() => setMuted((m) => !m)}
          onPrev={() => setActive((i) => Math.max(0, i - 1))}
          onNext={() => setActive((i) => Math.min(shorts.length - 1, i + 1))}
          hasPrev={active > 0}
          hasNext={active < shorts.length - 1}
          index={active}
          total={shorts.length}
        />
      )}

      {showCreate && (
        <CreateShortModal
          onClose={() => setShowCreate(false)}
          onCreated={(s) => { setShowCreate(false); setShorts((arr) => [s, ...arr]); setActive(0); toast.success("Short publié !"); }}
        />
      )}
    </div>
  );
};

// ─────────── Empty state ───────────
const EmptyState = ({ onCreate }) => (
  <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6">
    <div className="w-24 h-24 mb-6 rounded-3xl bg-gradient-to-br from-brand-red/30 to-brand-yellow/20 border border-white/10 flex items-center justify-center">
      <Music size={40} className="text-brand-red" />
    </div>
    <p className="text-white text-2xl font-black mb-2" style={{ fontFamily: "'Anton', sans-serif" }}>AUCUN SHORT POUR L'INSTANT</p>
    <p className="text-white/55 text-sm max-w-xs mb-6">Sois le premier à publier un short et fais vibrer la communauté Voamada 🇲🇬</p>
    <button onClick={onCreate} className="px-6 py-3.5 rounded-full bg-brand-red hover:bg-brand-redHover text-white text-sm font-black uppercase tracking-widest transition shadow-lg shadow-brand-red/30">
      + Publier le premier short
    </button>
  </div>
);

// ─────────── Player vertical fullscreen ───────────
const ShortPlayer = ({ short, isActive, muted, onToggleMute, onPrev, onNext, hasPrev, hasNext, index, total }) => {
  const videoRef = useRef(null);
  const [paused, setPaused] = useState(false);
  const [error, setError] = useState(false);
  const [liked, setLiked] = useState(false);
  const [likes, setLikes] = useState(short?.likes_count || 0);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    setError(false);
    setLiked(false);
    setLikes(short?.likes_count || 0);
    setPaused(false);
  }, [short?.track_id, short?.likes_count]);

  useEffect(() => {
    if (!videoRef.current) return;
    videoRef.current.muted = muted;
    if (isActive && !paused) videoRef.current.play().catch(() => {});
    else videoRef.current.pause();
  }, [isActive, muted, paused, short?.track_id]);

  const toggleLike = async () => {
    if (!user) { navigate("/login?redirect=/shorts"); return; }
    setLiked((v) => !v);
    setLikes((n) => liked ? Math.max(0, n - 1) : n + 1);
    try { await api.post(`/tracks/${short.track_id}/like`); }
    catch { setLiked((v) => !v); setLikes((n) => liked ? n + 1 : Math.max(0, n - 1)); toast.error("Erreur"); }
  };

  const share = async () => {
    const url = `${window.location.origin}/shorts?id=${short.track_id}`;
    const data = { title: short.title, text: `${short.title} — par ${short.artist_name}`, url };
    if (navigator.share) { try { await navigator.share(data); return; } catch {} }
    try { await navigator.clipboard.writeText(url); toast.success("Lien copié !"); } catch { toast.error("Impossible de copier"); }
  };

  if (!short) return null;
  const isExternal = !!short.external_url;

  return (
    <div className="absolute inset-0">
      {/* Video / Embed */}
      <div className="absolute inset-0 flex items-center justify-center bg-black">
        {error ? (
          <div className="flex flex-col items-center justify-center text-center px-6">
            <AlertTriangle size={32} className="text-red-400 mb-2" />
            <p className="text-white font-bold text-sm">Vidéo illisible</p>
            <p className="text-white/55 text-xs mt-1 max-w-xs">Format non supporté par ton navigateur. L'auteur peut re-uploader en MP4 H.264.</p>
          </div>
        ) : isExternal ? (
          short.external_source === "tiktok" ? (
            <iframe src={short.external_url} className="w-full h-full max-h-screen border-0" allow="autoplay; encrypted-media; fullscreen" allowFullScreen title={short.title} />
          ) : short.external_source === "youtube" ? (
            <iframe src={`${short.external_url}&autoplay=1&playsinline=1&loop=1&playlist=${short.external_url.split('/embed/')[1]?.split('?')[0]}`}
              className="w-full h-full max-h-screen aspect-[9/16] border-0" allow="autoplay; encrypted-media" allowFullScreen title={short.title} />
          ) : (
            <iframe src={short.external_url} className="w-full h-full max-h-screen border-0" allow="autoplay; encrypted-media" allowFullScreen title={short.title} />
          )
        ) : (
          <video
            ref={videoRef}
            src={fileStreamUrl(short.file_path)}
            poster={short.cover_path ? fileStreamUrl(short.cover_path) : undefined}
            className="max-h-full max-w-full object-contain"
            playsInline
            loop
            preload="metadata"
            onClick={() => setPaused((p) => !p)}
            onError={() => setError(true)}
          />
        )}
        {paused && !error && !isExternal && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <Play size={72} className="text-white/85 drop-shadow-2xl" />
          </div>
        )}
      </div>

      {/* Sidebar actions (droite) */}
      <div className="absolute right-3 bottom-28 sm:bottom-12 flex flex-col items-center gap-5 z-20">
        <button onClick={toggleLike} className="flex flex-col items-center text-white" data-testid="short-like-btn">
          <div className={`w-12 h-12 rounded-full backdrop-blur-md flex items-center justify-center transition ${liked ? "bg-brand-red text-white" : "bg-white/15 hover:bg-white/25"}`}>
            <Heart size={22} fill={liked ? "currentColor" : "none"} />
          </div>
          <span className="text-[11px] font-black mt-1 drop-shadow-lg">{likes}</span>
        </button>
        <Link to={`/track/${short.track_id}`} className="flex flex-col items-center text-white" data-testid="short-comments-btn">
          <div className="w-12 h-12 rounded-full bg-white/15 hover:bg-white/25 backdrop-blur-md flex items-center justify-center transition">
            <MessageCircle size={22} />
          </div>
          <span className="text-[11px] font-black mt-1 drop-shadow-lg">{short.comments_count || 0}</span>
        </Link>
        <button onClick={share} className="flex flex-col items-center text-white" data-testid="short-share-btn">
          <div className="w-12 h-12 rounded-full bg-white/15 hover:bg-white/25 backdrop-blur-md flex items-center justify-center transition">
            <Share2 size={22} />
          </div>
          <span className="text-[11px] font-black mt-1 drop-shadow-lg">Partager</span>
        </button>
        {!isExternal && (
          <button onClick={onToggleMute} className="flex flex-col items-center text-white" data-testid="short-mute-btn">
            <div className="w-12 h-12 rounded-full bg-white/15 hover:bg-white/25 backdrop-blur-md flex items-center justify-center transition">
              {muted ? <VolumeX size={20} /> : <Volume2 size={20} />}
            </div>
          </button>
        )}
      </div>

      {/* Navigation arrows (desktop) */}
      <div className="hidden sm:flex absolute right-3 top-1/2 -translate-y-1/2 flex-col gap-2 z-20">
        <button onClick={onPrev} disabled={!hasPrev} className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/25 disabled:opacity-30 flex items-center justify-center text-white transition backdrop-blur-md">
          <ChevronUp size={20} />
        </button>
        <button onClick={onNext} disabled={!hasNext} className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/25 disabled:opacity-30 flex items-center justify-center text-white transition backdrop-blur-md">
          <ChevronDown size={20} />
        </button>
      </div>

      {/* Counter (top right) */}
      <div className="absolute top-20 right-4 px-2.5 py-1 rounded-full bg-black/50 backdrop-blur text-[10px] font-black text-white/85 z-20">
        {index + 1} / {total}
      </div>

      {/* Bottom info */}
      <div className="absolute left-0 right-16 bottom-0 p-4 pb-6 bg-gradient-to-t from-black/95 via-black/50 to-transparent z-10">
        <Link to={`/u/${short.user_id}`} className="inline-flex items-center gap-2 mb-2 group">
          {short.user_picture || short.artist_picture ? (
            <img src={short.user_picture || short.artist_picture} alt="" className="w-9 h-9 rounded-full object-cover border border-white/30" />
          ) : (
            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-brand-red to-brand-yellow border border-white/30 flex items-center justify-center text-white text-xs font-black">
              {(short.user_name || short.artist_name || "?").charAt(0).toUpperCase()}
            </div>
          )}
          <span className="text-white font-black text-sm group-hover:underline">@{short.user_name || short.artist_name}</span>
        </Link>
        <p className="text-white font-bold text-base leading-tight mb-1">{short.title}</p>
        {short.description && <p className="text-white/75 text-xs line-clamp-2">{short.description}</p>}
        {isExternal && (
          <span className="inline-flex items-center gap-1 mt-2 px-2 py-0.5 rounded bg-white/15 text-[10px] text-white/85 font-black uppercase tracking-widest">
            {short.external_source}
          </span>
        )}
      </div>
    </div>
  );
};

// ─────────── Modal de création ───────────
const CreateShortModal = ({ onClose, onCreated }) => {
  const [mode, setMode] = useState("upload"); // 'upload' | 'external'
  const [file, setFile] = useState(null);
  const [url, setUrl] = useState("");
  const [title, setTitle] = useState("");
  const [desc, setDesc] = useState("");
  const [uploading, setUploading] = useState(false);

  const submit = async () => {
    if (!title.trim()) { toast.error("Titre requis"); return; }
    setUploading(true);
    try {
      if (mode === "upload") {
        if (!file) { toast.error("Choisis un fichier vidéo"); setUploading(false); return; }
        const ext = (file.name.split(".").pop() || "").toLowerCase();
        if (!["mp4", "m4v", "mov", "webm"].includes(ext)) { toast.error(`Format .${ext} non supporté.`); setUploading(false); return; }
        if (file.size > 50 * 1024 * 1024) { toast.error("Vidéo trop lourde (max 50 Mo)."); setUploading(false); return; }
        const fd = new FormData();
        fd.append("file", file);
        fd.append("title", title.trim());
        fd.append("description", desc.trim());
        const { data } = await api.post("/shorts/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        onCreated(data.short);
      } else {
        if (!url.trim()) { toast.error("URL requise"); setUploading(false); return; }
        const { data } = await api.post("/shorts/external", { url: url.trim(), title: title.trim(), description: desc.trim() });
        onCreated(data.short);
      }
    } catch (e) {
      toast.error(e?.response?.data?.error || "Erreur");
    } finally { setUploading(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/85 backdrop-blur flex items-end sm:items-center justify-center p-0 sm:p-6" onClick={onClose} data-testid="short-create-modal">
      <div onClick={(e) => e.stopPropagation()} className="bg-zinc-950 border border-white/10 rounded-t-3xl sm:rounded-3xl w-full max-w-md max-h-[90vh] overflow-y-auto p-6 shadow-2xl">
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-black text-xl" style={{ fontFamily: "'Anton', sans-serif" }}>📱 NOUVEAU SHORT</h3>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-white/10 transition"><X size={18} /></button>
        </div>

        {/* Switch mode */}
        <div className="grid grid-cols-2 gap-2 mb-5">
          <button onClick={() => setMode("upload")} className={`px-3 py-2.5 rounded-lg text-xs font-black uppercase tracking-widest transition ${mode === "upload" ? "bg-brand-red text-white" : "bg-white/5 hover:bg-white/10 text-white/65"}`}>
            <UploadIcon size={12} className="inline -mt-0.5 mr-1" /> Upload
          </button>
          <button onClick={() => setMode("external")} className={`px-3 py-2.5 rounded-lg text-xs font-black uppercase tracking-widest transition ${mode === "external" ? "bg-brand-red text-white" : "bg-white/5 hover:bg-white/10 text-white/65"}`}>
            <LinkIcon size={12} className="inline -mt-0.5 mr-1" /> URL externe
          </button>
        </div>

        {/* Form */}
        {mode === "upload" ? (
          <div className="mb-4">
            <label className="block text-[10px] uppercase tracking-widest text-white/55 font-bold mb-1.5">Vidéo verticale 9:16 *</label>
            <input
              type="file"
              accept="video/mp4,video/x-m4v,video/quicktime,video/webm,.mp4,.m4v,.mov,.webm"
              onChange={(e) => {
                const f = e.target.files?.[0]; if (!f) return setFile(null);
                if (f.size > 50 * 1024 * 1024) { toast.error("Trop lourd (max 50 Mo)"); e.target.value = ""; return; }
                setFile(f);
              }}
              className="w-full text-xs text-white/75 file:mr-2 file:py-2 file:px-3 file:rounded-md file:border-0 file:bg-brand-red file:text-white file:font-black file:uppercase file:text-[10px] file:tracking-widest hover:file:bg-brand-redHover cursor-pointer"
              data-testid="short-upload-file"
            />
            <p className="text-[10px] text-white/45 mt-1.5">MP4 H.264 · 60s max · 50 Mo max · format vertical 9:16</p>
          </div>
        ) : (
          <div className="mb-4">
            <label className="block text-[10px] uppercase tracking-widest text-white/55 font-bold mb-1.5">URL TikTok / YouTube Shorts / Instagram Reels *</label>
            <input
              type="url"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://www.tiktok.com/@... ou https://youtube.com/shorts/..."
              className="w-full px-3 py-2.5 bg-white/[0.04] border border-white/10 rounded-lg text-sm focus:border-brand-red focus:outline-none"
              data-testid="short-external-url"
            />
            <p className="text-[10px] text-white/45 mt-1.5">Le short s'affichera via le lecteur officiel de la plateforme.</p>
          </div>
        )}

        <div className="mb-4">
          <label className="block text-[10px] uppercase tracking-widest text-white/55 font-bold mb-1.5">Titre *</label>
          <input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={120} placeholder="Ex: Freestyle au Rova" className="w-full px-3 py-2.5 bg-white/[0.04] border border-white/10 rounded-lg text-sm focus:border-brand-red focus:outline-none" data-testid="short-title-input" />
        </div>
        <div className="mb-5">
          <label className="block text-[10px] uppercase tracking-widest text-white/55 font-bold mb-1.5">Description (optionnel)</label>
          <textarea value={desc} onChange={(e) => setDesc(e.target.value)} maxLength={500} rows={2} placeholder="Hashtags, contexte, crédit prod..." className="w-full px-3 py-2.5 bg-white/[0.04] border border-white/10 rounded-lg text-sm focus:border-brand-red focus:outline-none resize-none" />
        </div>

        <button onClick={submit} disabled={uploading} className="w-full px-6 py-3.5 rounded-full bg-brand-red hover:bg-brand-redHover disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm font-black uppercase tracking-widest transition" data-testid="short-publish-btn">
          {uploading ? "Publication…" : "Publier le short"}
        </button>
      </div>
    </div>
  );
};

export default ShortsPage;
